import {
  Box,
  BriefcaseBusiness,
  CalendarDays,
  ClipboardCheck,
  Eye,
  FileText,
  Layers,
  Mail,
  Monitor,
  Package,
  PenLine,
  Play,
  Printer,
  Search,
  Shuffle,
  Square,
  Tag,
  Truck,
  X,
  type LucideIcon
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { formatClock, formatMinutes } from "../../shared/calculations";
import type { ActivityType, JobTimeEntry, Order } from "../../shared/types";
import { findOrderByNumber } from "../../shared/jobOrderSelection";

type OrderWithActivities = Order & { activities: ActivityType[] };

type Payload = {
  order_refresh?: { updated_at: string | null; refreshing: boolean; stale: boolean; error: string | null };
  orders: OrderWithActivities[];
  recent_order_ids?: string[];
  activity_types: ActivityType[];
  open_entry: JobTimeEntry | null;
  today_entries: JobTimeEntry[];
};

async function api<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    ...options,
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(options?.headers ?? {}) }
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({ error: "Anfrage fehlgeschlagen" }));
    throw new Error(payload.error ?? "Anfrage fehlgeschlagen");
  }
  return response.json() as Promise<T>;
}

export function TerminalJobTimePanel({ employeeId, terminalStatus }: { employeeId: string; terminalStatus: "present" | "interrupted" }) {
  const [payload, setPayload] = useState<Payload | null>(null);
  const [selectedOrderId, setSelectedOrderId] = useState("");
  const [selectedActivityId, setSelectedActivityId] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerStep, setPickerStep] = useState<"orders" | "activities">("orders");
  const [searchTerm, setSearchTerm] = useState("");
  const [orderNumber, setOrderNumber] = useState("");
  const [numberError, setNumberError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const employeeRef = useRef(employeeId);
  employeeRef.current = employeeId;
  const requestGeneration = useRef(0);

  function applyPayload(result: Payload) {
    const orders = [...result.orders].sort((a, b) => a.job_number.localeCompare(b.job_number, "de", { numeric: true }));
    setPayload({ ...result, orders });
    setSelectedOrderId((current) => orders.some((order) => order.id === current) ? current : "");
  }

  async function load(force = false) {
    const generation = ++requestGeneration.current;
    const employee = employeeId;
    const result = await api<Payload>(`/api/job-times/terminal/${employee}${force ? "?refresh=1" : ""}`);
    if (employeeRef.current !== employee || generation !== requestGeneration.current || busyRef.current) return;
    applyPayload(result);
  }

  useEffect(() => {
    setPayload(null);
    setSelectedOrderId("");
    setSelectedActivityId("");
    setOrderNumber("");
    setNumberError(null);
    setPickerOpen(false);
    let active = true;
    const refresh = () => {
      if (busyRef.current) return;
      void load().catch((error) => { if (active) setMessage(error instanceof Error ? error.message : "Aufträge konnten nicht geladen werden."); });
    };
    refresh();
    const interval = window.setInterval(refresh, 15_000);
    return () => { active = false; window.clearInterval(interval); requestGeneration.current++; };
  }, [employeeId]);

  const selectedOrder = useMemo(() => payload?.orders.find((order) => order.id === selectedOrderId) ?? null, [payload?.orders, selectedOrderId]);
  const selectedActivity = useMemo(() => selectedOrder?.activities.find((activity) => activity.id === selectedActivityId) ?? null, [selectedActivityId, selectedOrder]);
  const filteredOrders = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!payload) return [];
    if (!query) return payload.orders;
    return payload.orders.filter((order) =>
      [order.job_number, order.customer_name, order.order_title, order.due_date ?? ""].some((value) => value.toLowerCase().includes(query))
    );
  }, [payload, searchTerm]);
  const openOrder = payload?.orders.find((order) => order.id === payload.open_entry?.order_id);
  const openActivity = payload?.activity_types.find((activity) => activity.id === payload.open_entry?.activity_type_id);
  const recentOrders = (payload?.recent_order_ids ?? []).flatMap(id => {
    const order = payload?.orders.find(item => item.id === id);
    return order ? [order] : [];
  });

  useEffect(() => {
    if (!selectedOrder) return;
    if (!selectedOrder.activities.some((activity) => activity.id === selectedActivityId)) {
      setSelectedActivityId(selectedOrder.activities[0]?.id ?? "");
    }
  }, [selectedOrderId, selectedOrder, selectedActivityId]);

  async function run(action: "start" | "stop" | "switch") {
    if (busyRef.current) return;
    busyRef.current = true;
    requestGeneration.current++;
    const employee = employeeId;
    setBusy(true);
    setMessage(null);
    try {
      const body =
        action === "stop"
          ? { employeeId }
          : {
              employeeId,
              orderId: selectedOrderId,
              activityTypeId: selectedActivityId
            };
      const result = await api<Payload>(`/api/job-times/${action}`, { method: "POST", body: JSON.stringify(body) });
      if (employeeRef.current === employee) applyPayload(result);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Auftragszeit konnte nicht gebucht werden.");
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }

  function openPicker(step: "orders" | "activities" = "orders") {
    setPickerStep(step);
    setSearchTerm("");
    setPickerOpen(true);
  }

  function chooseOrder(order: OrderWithActivities) {
    setSelectedOrderId(order.id);
    setOrderNumber(order.job_number);
    setSelectedActivityId(order.activities[0]?.id ?? "");
    setPickerStep("activities");
    setSearchTerm("");
    setNumberError(null);
    setPickerOpen(true);
  }

  function selectOrderNumber() {
    if (busyRef.current) return;
    const result = findOrderByNumber(payload?.orders ?? [], orderNumber);
    if (result.kind === "found") {
      chooseOrder(result.order);
      return;
    }
    setSelectedOrderId("");
    setSelectedActivityId("");
    setNumberError(result.kind === "empty" ? "Bitte die vollständige Auftragsnummer eingeben."
      : result.kind === "ambiguous" ? "Diese Nummer ist nicht eindeutig. Bitte über die Jobauswahl prüfen."
      : "Kein aktuell auswählbarer Auftrag mit dieser Nummer. Bitte Nummer prüfen oder Aufträge aktualisieren.");
  }

  function chooseActivity(activity: ActivityType) {
    setSelectedActivityId(activity.id);
    setPickerOpen(false);
  }

  if (!payload) return <div className="job-terminal-panel">{message || "Auftragszeiten werden geladen…"}<button type="button" onClick={() => void load(true).catch((error) => setMessage(error.message))}>Erneut laden</button></div>;

  return (
    <div className="job-terminal-panel">
      <div className="job-terminal-head">
        <BriefcaseBusiness />
        <h2>Auftragszeit</h2>
      </div>

      <div className="job-order-refresh" role="status">
        <small>{payload.order_refresh?.updated_at ? `Auftragsstand: ${new Date(payload.order_refresh.updated_at).toLocaleTimeString("de-DE")}` : "Auftragsstand wird geprüft"}{payload.order_refresh?.refreshing ? " · wird aktualisiert" : ""}</small>
        <button type="button" disabled={busy} onClick={() => void load(true).catch((error) => setMessage(error.message))}>Aufträge aktualisieren</button>
        {(payload.order_refresh?.error || payload.order_refresh?.stale) && <p className="form-error">{payload.order_refresh.error || "Auftragsstand ist älter. Vor einer neuen Buchung wird aktualisiert."}</p>}
      </div>
      {terminalStatus === "interrupted" && <p className="form-error">Unterbrechung aktiv. Auftragszeitbuchungen sind gesperrt.</p>}

      {payload.open_entry && (
        <div className="active-job-band">
          <span>Aktuell</span>
          <strong>
            {openOrder?.job_number ?? "-"} · {openActivity?.name ?? "-"} · seit {formatClock(payload.open_entry.start_time)}
          </strong>
        </div>
      )}

      {terminalStatus === "present" && (
        <>
          {payload.orders.length === 0 && <p className="form-error">Keine aktiven Aufträge im PAL-Fenster gefunden.</p>}
          <div className="job-number-entry">
            <label htmlFor="terminal-order-number">Auftragsnummer direkt eingeben</label>
            <div className="job-number-entry-controls">
              <input id="terminal-order-number" value={orderNumber} disabled={busy} maxLength={40} inputMode="numeric"
                autoComplete="off" placeholder="z. B. 10-070-26" aria-describedby="terminal-order-number-hint"
                onChange={event => { setOrderNumber(event.target.value); setNumberError(null); }}
                onKeyDown={event => { if (event.key === "Enter") { event.preventDefault(); selectOrderNumber(); } }} />
              <button type="button" className="button secondary" disabled={busy} onClick={selectOrderNumber}>Auftrag auswählen</button>
            </div>
            <small id="terminal-order-number-hint">Vollständige Nummer, auch ohne Bindestriche. Danach Tätigkeit wählen und Buchung bestätigen.</small>
            {numberError && <p className="form-error" role="alert">{numberError}</p>}
          </div>
          <section className="job-recent-orders" aria-label="Meine letzten Aufträge">
            <h3>Meine letzten Aufträge</h3>
            {recentOrders.length ? <div className="job-recent-grid">{recentOrders.map(order => (
              <button key={order.id} type="button" className="job-recent-button" disabled={busy} onClick={() => chooseOrder(order)}>
                <strong>{order.job_number}</strong><span>{order.order_title}</span><small>{order.customer_name}</small>
              </button>
            ))}</div> : <p>Noch keine zuletzt verwendeten, aktuell auswählbaren Aufträge.</p>}
          </section>
          <div className="job-touch-selection">
            <button className="job-picker-open" disabled={payload.orders.length === 0} onClick={() => openPicker("orders")} type="button">
              <BriefcaseBusiness />
              <span>Jobauswahl</span>
              <strong>{selectedOrder ? `${selectedOrder.job_number} · ${selectedOrder.order_title}` : "Job auswählen"}</strong>
              <small>{selectedActivity?.name ?? "Tätigkeit danach auswählen"}</small>
            </button>
            <button className="job-activity-open" disabled={!selectedOrder} onClick={() => openPicker("activities")} type="button">
              <ClipboardCheck />
              <span>Tätigkeit wählen</span>
            </button>
          </div>
          <div className="terminal-actions compact-actions">
            {!payload.open_entry && (
              <button className="button primary" disabled={busy || !selectedOrderId || !selectedActivityId} onClick={() => void run("start")}>
                <Play />
                <span>Auftrag starten</span>
              </button>
            )}
            {payload.open_entry && (
              <>
                <button className="button secondary" disabled={busy || !selectedOrderId || !selectedActivityId} onClick={() => void run("switch")}>
                  <Shuffle />
                  <span>Auftrag wechseln</span>
                </button>
                <button className="button primary" disabled={busy} onClick={() => void run("stop")}>
                  <Square />
                  <span>Auftragszeit stoppen</span>
                </button>
              </>
            )}
          </div>
        </>
      )}

      <details className="job-today-details">
        <summary>Meine Auftragszeiten heute</summary>
        <div className="mini-job-list">
          {payload.today_entries.map((entry) => {
            const order = payload.orders.find((item) => item.id === entry.order_id);
            const activity = payload.activity_types.find((item) => item.id === entry.activity_type_id);
            return (
              <div key={entry.id}>
                <span>{order?.job_number ?? "-"}</span>
                <strong>{activity?.name ?? "-"}</strong>
                <span>{entry.status === "running" ? "läuft" : formatMinutes(entry.duration_minutes)}</span>
              </div>
            );
          })}
        </div>
      </details>
      {message && <p className="form-error">{message}</p>}
      {pickerOpen && (
        <div className="job-picker-overlay" role="dialog" aria-modal="true" aria-label="Jobauswahl">
          <div className="job-picker-shell">
            <header className="job-picker-header">
              <div>
                <span>{pickerStep === "orders" ? "Schritt 1 von 2" : "Schritt 2 von 2"}</span>
                <h2>{pickerStep === "orders" ? "Job auswählen" : "Tätigkeit auswählen"}</h2>
              </div>
              <button className="job-picker-close" onClick={() => setPickerOpen(false)} type="button" aria-label="Auswahl schließen">
                <X />
              </button>
            </header>

            {pickerStep === "orders" && (
              <>
                <label className="job-picker-search">
                  <Search />
                  <input value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} placeholder="Jobnummer, Kunde oder Auftragsname" />
                </label>
                <div className="job-picker-grid">
                  {filteredOrders.map((order) => (
                    <button className={`job-tile ${order.id === selectedOrderId ? "selected" : ""}`} key={order.id} onClick={() => chooseOrder(order)} type="button">
                      <span className="job-tile-number">{order.job_number}</span>
                      <strong>{order.order_title}</strong>
                      <span>{order.customer_name}</span>
                      <small>
                        <CalendarDays /> PAL {order.due_date ?? "-"}
                      </small>
                    </button>
                  ))}
                </div>
              </>
            )}

            {pickerStep === "activities" && selectedOrder && (
              <>
                <div className="job-picker-selected-job">
                  <div>
                    <span>{selectedOrder.job_number}</span>
                    <strong>{selectedOrder.order_title}</strong>
                  </div>
                  <button className="button secondary" onClick={() => setPickerStep("orders")} type="button">
                    Job ändern
                  </button>
                </div>
                <div className="activity-tile-grid">
                  {selectedOrder.activities.map((activity) => {
                    const visual = activityVisual(activity.name);
                    const Icon = visual.icon;
                    return (
                      <button className={`activity-tile ${activity.id === selectedActivityId ? "selected" : ""}`} key={activity.id} onClick={() => chooseActivity(activity)} type="button">
                        <span className={`activity-picture ${visual.kind}`}>
                          <Icon />
                        </span>
                        <strong>{activity.name}</strong>
                        <span>{activity.id === selectedActivityId ? "ausgewählt" : "auswählen"}</span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function activityVisual(name: string): { icon: LucideIcon; kind: string } {
  const value = name.trim().toLowerCase();
  if (value.includes("edv") || value.includes("daten") || value.includes("digital")) return { icon: Monitor, kind: "edv" };
  if (value.includes("tabb")) return { icon: Tag, kind: "mark" };
  if (value.includes("laser") || value.includes("druck") || value.includes("print")) return { icon: Printer, kind: "print" };
  if (value.includes("inkjet") || value.includes("etikett") || value.includes("label")) return { icon: Tag, kind: "mark" };
  if (value.includes("falz") || value.includes("falten")) return { icon: Layers, kind: "fold" };
  if (value.includes("kuvert") || value.includes("brief") || value.includes("mail")) return { icon: Mail, kind: "mail" };
  if (value.includes("post") || value.includes("transport") || value.includes("empfänger")) return { icon: Truck, kind: "ship" };
  if (value.includes("paket") || value.includes("dhl")) return { icon: Package, kind: "pack" };
  if (value.includes("vergleich") || value.includes("lesung") || value.includes("prüf")) return { icon: Eye, kind: "check" };
  if (value.includes("konfektion") || value.includes("pack")) return { icon: Box, kind: "box" };
  if (value.includes("scan") || value.includes("code")) return { icon: FileText, kind: "scan" };
  if (value.includes("schreib") || value.includes("manuell")) return { icon: PenLine, kind: "manual" };
  return { icon: ClipboardCheck, kind: "other" };
}
